import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MaintenanceDetails } from '../../../interface/maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllStationsList } from '../../../redux/slice/ApprovedStationManagementSlice';
import { fetchAllDevicesList } from '../../../redux/slice/DeviceManagementSlice';
import { fetchAllMaintenanceSchedules } from '../../../redux/slice/MaintenanceScheduleManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomTimeInput from '../../../shared-components/custom-time-input/CustomTimePicker';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';

import MaintenanceContent from './MaintenanceContent';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface MaintenanceSchedulingProperties {
  onClose?: () => void;
  onSuccess?: () => void;
}

/**
 * Add new maintenance
 */
function MaintenanceScheduling({ onClose, onSuccess }: MaintenanceSchedulingProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('maintenanceSchedule');
  const { allStations, loading } = useAppSelector((state) => state.approvedStation);
  const { allDevices } = useAppSelector((state) => state.device);
  const { page, limit } = useAppSelector((state) => state.maintenanceSchedule);

  const navigate = useNavigate();
  const [maintenanceDetails, setMaintenanceDetails] = useState<MaintenanceDetails>({
    station: [],
    device: [],
    fromDate: '',
    time: '',
    maintenance: '',
    priority: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [showFormErrors, setShowFormErrors] = useState(false);

  const typeOfMaintenanceOptions = [
    {
      label: t('inspection'),
      value: 'inspection',
    },
    {
      label: t('repair'),
      value: 'repair',
    },
    {
      label: t('softwareUpdate'),
      value: 'softwareupdate',
    },
  ];
  const priorityOptions = [
    {
      label: t('high'),
      value: 'high',
    },
    {
      label: t('low'),
      value: 'low',
    },
  ];

  /**
   * Fetch the all station function
   */
  const fetchStation = () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }
  };

  /**
   * Handle input value change
   * @param {string} name - Name of the input
   * @param {string | number | string[]} changeValue - Value of the input
   */
  const handleMaintenanceDetailChange = (name: string, changeValue: string | number | string[]) => {
    setMaintenanceDetails({
      ...maintenanceDetails,
      [name]: changeValue,
    });
    if (name === 'station') {
      if (Array.isArray(changeValue)) {
        dispatch(fetchAllDevicesList({ ids: changeValue }));
      } else if (changeValue) {
        dispatch(fetchAllDevicesList({ ids: [changeValue] }));
      } else {
        dispatch(fetchAllDevicesList({ ids: [] }));
      }
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setShowFormErrors(false);
    setMaintenanceDetails({
      station: [],
      device: [],
      fromDate: '',
      time: '',
      maintenance: '',
      priority: '',
    });
    setModalBoxStatus('close');

    if (onClose) {
      onClose();
    } else {
      navigate('../schedule');
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setShowFormErrors(false);
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('formCancelWarningTitle')}
          description={t('formCancelWarningDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('oK')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Checks if all fields in the maintenance details are filled
   */
  const isFormComplete = () => {
    return Object.values(maintenanceDetails).every((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '';
    });
  };

  /**
   * Function to show success message
   */
  const openSuccessModal = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <SuccessNotification
          title={t('successNotificationTitle')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setModalBoxStatus('close');
            if (onSuccess) {
              onSuccess();
            } else {
              dispatch(
                fetchAllMaintenanceSchedules({
                  page,
                  limit,
                }),
              );
            }
            if (onClose) {
              onClose();
            } else {
              navigate('../maintenance');
            }
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Submit to open the modal
   */
  const handleSubmitMaintenanceDetails = () => {
    setShowFormErrors(true);
    if (!isFormComplete()) return;

    setModalBoxDetail({
      title: t('maintenanceScheduled'),
      content: (
        <MaintenanceContent
          maintenanceDetails={maintenanceDetails}
          onChangeValue={handleMaintenanceDetailChange}
          closeModal={() => setModalBoxStatus('close')}
          openSuccessModal={openSuccessModal}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  useEffect(() => {
    if (allStations.length === 0) {
      fetchStation();
    }
  }, []);

  // Cleanup selected devices when the available devices list changes
  useEffect(() => {
    const availableDeviceValues = allDevices.map((device) => device.value.toString());
    const updatedSelectedDevices = maintenanceDetails.device.filter((selectedDevice) =>
      availableDeviceValues.includes(selectedDevice),
    );

    if (updatedSelectedDevices.length !== maintenanceDetails.device.length) {
      setMaintenanceDetails((previous) => ({
        ...previous,
        device: updatedSelectedDevices,
      }));
    }
  }, [allDevices]);

  return (
    <div className="add-maintenance-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <p className="add-maintenance-title">{t('addSchedule')}</p>
      <div className="add-maintenance-container">
        <div className="add-maintenance-from-wrap">
          <div className="add-maintenance-from">
            <MultiSelectDropdown
              title={t('stationName')}
              name="station"
              placeholder={t('chooseStation')}
              placeholderFilled={t('stations')}
              selectedValues={maintenanceDetails.station}
              onSelectionChange={handleMaintenanceDetailChange}
              options={allStations}
              error={maintenanceDetails.station.length === 0 && showFormErrors}
              errorMessage={t('stationNameIsRequired')}
              search
            />
            <MultiSelectDropdown
              title={t('device')}
              name="device"
              placeholder={t('chooseDevice')}
              placeholderFilled={t('devices')}
              selectedValues={maintenanceDetails.device}
              onSelectionChange={handleMaintenanceDetailChange}
              options={allDevices}
              error={maintenanceDetails.device.length === 0 && showFormErrors}
              errorMessage={t('deviceIsRequired')}
              search
            />
            <CustomDatePicker
              name="fromDate"
              inputLabel={t('date')}
              value={maintenanceDetails.fromDate}
              onChangeValue={handleMaintenanceDetailChange}
              error={!maintenanceDetails.fromDate && showFormErrors}
              errorMessage={t('invalidDateRange')}
              disablePastDates
            />
            <CustomTimeInput
              name="time"
              inputLabel={t('time')}
              value={maintenanceDetails.time}
              onChangeValue={handleMaintenanceDetailChange}
              errorHandle={!maintenanceDetails.time && showFormErrors}
              // errorMessage="Invalid time range"
            />
            <SelectFiled
              inputLabel={t('typeOfMaintenance')}
              name="maintenance"
              placeholder={t('chooseTypeOfMaintenance')}
              selectedValue={maintenanceDetails.maintenance}
              onChangeValue={handleMaintenanceDetailChange}
              options={typeOfMaintenanceOptions}
              error={!maintenanceDetails.maintenance && showFormErrors}
              errorMessage={t('typeOfMaintenanceIsRequired')}
            />
            <SelectFiled
              inputLabel={t('priority')}
              name="priority"
              placeholder={t('choosePriority')}
              selectedValue={maintenanceDetails.priority}
              onChangeValue={handleMaintenanceDetailChange}
              options={priorityOptions}
              error={!maintenanceDetails.priority && showFormErrors}
              errorMessage={t('priorityIsRequired')}
            />
          </div>
        </div>
        <div className="add-maintenance-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label={t('schedule')}
              buttonType="submit"
              onClickFunc={handleSubmitMaintenanceDetails}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceScheduling;

MaintenanceScheduling.defaultProps = {
  /**
   * Function to handle close
   */
  onClose: () => {},
  /**
   * Function to handle success
   */
  onSuccess: () => {},
};
